import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { clamp } from "../core/math.js";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const sectionAtmosphere = {
  hero: {
    brightness: 0.96,
    contrast: 1.02,
    saturation: 0.92,
    mist: 0.04
  },
  about: {
    brightness: 0.9,
    contrast: 1.04,
    saturation: 0.88,
    mist: 0.08
  },
  projects: {
    brightness: 0.84,
    contrast: 1.1,
    saturation: 0.86,
    mist: 0.06
  },
  skills: {
    brightness: 0.8,
    contrast: 1.08,
    saturation: 0.82,
    mist: 0.11
  },
  timeline: {
    brightness: 0.76,
    contrast: 1.1,
    saturation: 0.78,
    mist: 0.13
  },
  contact: {
    brightness: 0.7,
    contrast: 1.12,
    saturation: 0.72,
    mist: 0.16
  }
};

const sectionMeta = [
  { id: "hero", label: "首页" },
  { id: "about", label: "关于" },
  { id: "projects", label: "作品" },
  { id: "skills", label: "能力" },
  { id: "timeline", label: "轨迹" },
  { id: "contact", label: "联系" }
];

export class ScrollController {
  #store;
  #tier;
  #reduced;
  #smoother = null;
  #triggers = [];
  #cleanup = [];
  #velocity = 0;
  #lastScrollY = window.scrollY;
  #lastScrollAt = performance.now();
  #backToTop;
  #sections = [];

  constructor({ store, tier, reduced, backToTop }) {
    this.#store = store;
    this.#tier = tier;
    this.#reduced = reduced;
    this.#backToTop = backToTop;
  }

  init() {
    if (!this.#reduced) {
      this.#initSmoother();
      this.#initReveals();
    } else {
      gsap.set(".reveal", { opacity: 1, y: 0 });
    }
    this.#initSections();
    this.#initNavigation();
    this.#initSkillLines();
    this.#initTimeline();
    ScrollTrigger.refresh();
  }

  #initSmoother() {
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    if (
      finePointer &&
      window.innerWidth >= 768 &&
      !["low", "reduced"].includes(this.#tier)
    ) {
      this.#smoother = ScrollSmoother.create({
        wrapper: "#smooth-wrapper",
        content: "#smooth-content",
        smooth: this.#tier === "high" ? 0.95 : 0.75,
        effects: true,
        smoothTouch: 0
      });
    }
  }

  #initReveals() {
    const heroElements = [...document.querySelectorAll(".hero .reveal")];
    if (heroElements.length) {
      const heroTween = gsap.fromTo(
        heroElements,
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.82,
          delay: 0.18,
          stagger: 0.1,
          ease: "power2.out"
        }
      );
      this.#triggers.push(heroTween);
    }

    document.querySelectorAll(".reveal").forEach((element) => {
      if (element.closest(".hero")) {
        return;
      }
      const tween = gsap.fromTo(
        element,
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.82,
          ease: "power2.out",
          scrollTrigger: {
            trigger: element,
            start: "top 88%",
            once: true
          }
        }
      );
      this.#triggers.push(tween.scrollTrigger);
    });
  }

  #initSections() {
    this.#sections = [...document.querySelectorAll("[data-section]")];
    this.#sections.forEach((section) => {
      const id = section.dataset.section;
      const trigger = ScrollTrigger.create({
        trigger: section,
        start: "top 55%",
        end: "bottom 45%",
        onToggle: ({ isActive }) => {
          if (isActive) {
            this.#activateSection(id);
          }
        }
      });
      this.#triggers.push(trigger);
    });
  }

  #activateSection(id) {
    const atmosphere = sectionAtmosphere[id] ?? sectionAtmosphere.hero;
    const index = Math.max(
      0,
      sectionMeta.findIndex((section) => section.id === id)
    );
    const current = sectionMeta[index];
    const next = sectionMeta[index + 1] ?? sectionMeta[0];
    this.#store.patch(
      {
        section: id,
        interaction: {
          sectionIndex: index,
          sectionCount: sectionMeta.length
        }
      },
      "section:change"
    );
    document.body.dataset.activeSection = id;
    this.#sections.forEach((section) => {
      const active = section.dataset.section === id;
      section.classList.toggle("is-section-active", active);
      if (active) {
        section.classList.add("is-section-visited");
      }
    });
    const progress = document.querySelector("[data-section-progress]");
    const label = document.querySelector("[data-section-label]");
    const nextLabel = document.querySelector("[data-next-section]");
    if (progress) {
      progress.textContent =
        `${String(index + 1).padStart(2, "0")} / ${String(sectionMeta.length).padStart(2, "0")}`;
    }
    if (label) {
      label.textContent = current.label;
    }
    if (nextLabel) {
      nextLabel.textContent =
        index === sectionMeta.length - 1
          ? "回到首页"
          : `下一章 · ${next.label}`;
    }
    document.querySelectorAll("[data-nav-section]").forEach((link) => {
      if (link.dataset.navSection === id) {
        link.setAttribute("aria-current", "page");
      } else {
        link.removeAttribute("aria-current");
      }
    });
    gsap.to(document.documentElement, {
      "--scene-brightness": atmosphere.brightness,
      "--scene-contrast": atmosphere.contrast,
      "--scene-saturation": atmosphere.saturation,
      "--mist-opacity": atmosphere.mist,
      duration: 1.15,
      ease: "sine.inOut",
      overwrite: true
    });
  }

  #initNavigation() {
    document.querySelectorAll("[data-scroll-to]").forEach((link) => {
      const handler = (event) => {
        const target = document.getElementById(link.dataset.scrollTo);
        if (!target) {
          return;
        }
        event.preventDefault();
        this.scrollTo(target);
      };
      link.addEventListener("click", handler);
      this.#cleanup.push(() => link.removeEventListener("click", handler));
    });

    const backHandler = () => {
      this.scrollTo(document.getElementById("hero"));
      gsap.fromTo(
        this.#backToTop,
        { rotate: 0 },
        { rotate: -100, duration: 0.7, ease: "power2.out" }
      );
    };
    this.#backToTop.addEventListener("click", backHandler);
    this.#cleanup.push(() =>
      this.#backToTop.removeEventListener("click", backHandler)
    );

    const scrollHandler = () => {
      const now = performance.now();
      const elapsed = Math.max(16, now - this.#lastScrollAt);
      const y = window.scrollY;
      this.#velocity = ((y - this.#lastScrollY) / elapsed) * 1000;
      this.#lastScrollY = y;
      this.#lastScrollAt = now;
    };
    window.addEventListener("scroll", scrollHandler, { passive: true });
    this.#cleanup.push(() =>
      window.removeEventListener("scroll", scrollHandler)
    );
  }

  #initSkillLines() {
    document.querySelectorAll(".skill-group").forEach((group) => {
      const trigger = ScrollTrigger.create({
        trigger: group,
        start: "top 78%",
        onEnter: () => group.classList.add("is-active"),
        onLeaveBack: () => group.classList.remove("is-active")
      });
      this.#triggers.push(trigger);
    });
  }

  #initTimeline() {
    const list = document.querySelector(".timeline-list");
    if (!list) {
      return;
    }
    if (this.#reduced) {
      list.style.setProperty("--timeline-progress", "1");
      return;
    }

    list.style.setProperty("--timeline-progress", "0");
    const growthTrigger = ScrollTrigger.create({
      trigger: list,
      start: "top 82%",
      end: "bottom 58%",
      scrub: 0.35,
      onUpdate: ({ progress }) => {
        list.style.setProperty("--timeline-progress", String(progress));
      }
    });
    this.#triggers.push(growthTrigger);

    list.querySelectorAll(".timeline-item").forEach((item) => {
      const itemTrigger = ScrollTrigger.create({
        trigger: item,
        start: "top 72%",
        onEnter: () => item.classList.add("is-passed"),
        onLeaveBack: () => item.classList.remove("is-passed")
      });
      this.#triggers.push(itemTrigger);
    });
  }

  scrollTo(target) {
    if (!target) {
      return;
    }
    if (this.#smoother && !this.#reduced) {
      this.#smoother.scrollTo(target, true, "top top");
    } else {
      target.scrollIntoView({
        behavior: this.#reduced ? "auto" : "smooth",
        block: "start"
      });
    }
  }

  pause(value) {
    this.#smoother?.paused(value);
  }

  update() {
    if (this.#smoother) {
      this.#velocity = this.#smoother.getVelocity();
    } else {
      this.#velocity *= 0.92;
    }
    const scrollTop = this.#smoother
      ? this.#smoother.scrollTop()
      : window.scrollY;
    const maximum =
      document.documentElement.scrollHeight - window.innerHeight;
    const progress = maximum > 0 ? scrollTop / maximum : 0;
    document.documentElement.style.setProperty(
      "--nav-progress",
      `${clamp(0, progress, 1) * 100}%`
    );
    this.#backToTop.classList.toggle("is-visible", progress > 0.12);
    return this.#velocity;
  }

  destroy() {
    this.#cleanup.forEach((cleanup) => cleanup());
    this.#triggers.forEach((trigger) => trigger?.kill());
    this.#smoother?.kill();
  }
}
